import { MetricsV1AggregationType } from '@spotify/confidence-graphql';

import { aggregationUtils } from '../../Aggregation';
import { RatioMeasurement, RatioMetricSpec } from './measurementInput';
import {
  applyCap,
  applyThreshold,
  coalesceToZeroIf,
  filterToSqlPredicate,
  filterToWhereClause,
  quoteIdentifier,
} from './sqlHelpers';

export type RatioSnippetArgs = {
  entity?: string | null;
  factTable?: string | null;
  measurement: RatioMeasurement;
};

const buildRatioExprs = (
  spec: RatioMetricSpec,
): { numerator: string; denominator: string } => {
  const numerator = spec.numerator?.name || '*';
  const denominator = spec.denominator?.name || '*';
  const numAgg = aggregationUtils.getAggregationTypeSqlLabel(
    spec.numeratorAggregation?.type ??
      MetricsV1AggregationType.AggregationTypeAvg,
  );
  const denomAgg = aggregationUtils.getAggregationTypeSqlLabel(
    spec.denominatorAggregation?.type ??
      MetricsV1AggregationType.AggregationTypeAvg,
  );
  const numeratorPredicate = filterToSqlPredicate(spec.numeratorFilter);
  const denominatorPredicate = filterToSqlPredicate(spec.denominatorFilter);
  const numCaseValue = numerator === '*' ? '1' : numerator;
  const denomCaseValue = denominator === '*' ? '1' : denominator;
  const numExpr = numeratorPredicate
    ? `${numAgg}(CASE WHEN ${numeratorPredicate} THEN ${numCaseValue} END)`
    : `${numAgg}(${numerator})`;
  const denomExpr = denominatorPredicate
    ? `${denomAgg}(CASE WHEN ${denominatorPredicate} THEN ${denomCaseValue} END)`
    : `${denomAgg}(${denominator})`;
  return {
    numerator: applyCap(
      applyThreshold(numExpr, spec.numeratorAggregation?.threshold),
      spec.numeratorAggregation?.cap,
    ),
    denominator: applyCap(
      applyThreshold(denomExpr, spec.denominatorAggregation?.threshold),
      spec.denominatorAggregation?.cap,
    ),
  };
};

export const ratioSnippet = ({
  entity,
  factTable,
  measurement,
}: RatioSnippetArgs): string => {
  const entityCol = quoteIdentifier(entity || 'entity');
  const factTableName = quoteIdentifier(factTable || 'fact_table');
  const replaceMissingWithZero =
    measurement.nullHandling?.replaceEntityNullWithZero ?? false;
  const exprs = buildRatioExprs(measurement.typeSpec.ratioMetricSpec);

  return `WITH measurements AS (
  SELECT
    ${entityCol},
    ${exprs.numerator} as numerator,
    ${exprs.denominator} as denominator
  FROM ${factTableName}${filterToWhereClause(measurement.filter)}
  GROUP BY ${entityCol}
)

SELECT
  SUM(${coalesceToZeroIf('numerator', replaceMissingWithZero)}) / SUM(${coalesceToZeroIf('denominator', replaceMissingWithZero)}) as metric
FROM measurements`;
};
